import React, { useCallback, useEffect, useContext, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Button, Checkbox, Paper, Tooltip } from '@material-ui/core';
import { MTableToolbar, Query } from '@material-table/core';
import { useFilter } from '../../../../../Components/ListFilter';
import { HtmlView } from '../../../../../Components/Html/Html.View';
import { MTable, RepealableName } from '../../../../../Components';
import { OwnNotes } from '../../../../../Components/OwnNotes';
import { LawListFilter } from './Filter';
import { useComplianceState } from './ComplianceState';
import { ModuleContext } from '../../../../../Context/ModuleContext/ModuleContext';
import { UserContext } from '../../../../../Context/UserContext/UserContext';
import { useAptorApi } from '../../../../../Api';
import { IGridQueryRequest, INamedEntity, OrderDirection } from '../../../../../Api/AptorApi';
import { useIsMounted } from '../../../../../Hooks/useIsMounted';
import { useStyles } from '../../../LawPortal.styles';
import { ArrowUpward, SaveAlt } from '@material-ui/icons';
import moment from 'moment';
import LatestComplianceControlDialog from "./LatestComplianceControlDialog";
import { useSnackbar } from 'notistack';
import { CustomerLawListActions } from '../CustomerLaws/CustomerLaws.List.Actions';

type LawData = {
  id: number;
  number: string;
  name: string;
  description: string;
  area: string;
  changeNumber?: string;
  changeEffectiveFrom?: Date;
  effectiveTo?: Date;
  companyUnits: Array<INamedEntity & { note: string | null }>;
};

export const LawList = () => {
  const { formatMessage } = useIntl();
  const intl = useIntl();
  const {
    enableCompliance,
    complianceEnabled,
    hasCompliance,
    isComplianceUserFor,
    toggleCompliance,
  } = useComplianceState();
  const { api } = useAptorApi();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [filters, setFilter] = useFilter();
  const tableRef = React.useRef<any>(null);
  const isMounted = useIsMounted();
  const { setHeader } = useContext(ModuleContext);
  const { getProcessName, getAspectName, getTagName } = useContext(UserContext);
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [lawIdForCompliance, setLawIdForCompliance] = useState("");
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // ✅ FIX: Separate hidden state from column definitions
  // This allows useMemo to recompute titles on language change
  // while still persisting user's show/hide preferences
  const [hiddenFields, setHiddenFields] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isMounted && isFiltersInitialized) {
      tableRef.current && (tableRef.current as any).onQueryChange();
    }
  }, [filters, tableRef, isMounted, isFiltersInitialized]);

  useEffect(() => {
    // setHeader(formatMessage({ id: 'law-portal.our-laws.header', defaultMessage: 'Our laws' }), [
    //   {
    //     title: formatMessage({ id: 'law-portal.our-law.tab.law-list' }),
    //     to: `/law-portal/our-laws`,
    //   },
    //   {
    //     title: formatMessage({ id: 'law-portal.statistics.sub-menu.changes.unacknowledged' }),
    //     to: `/law-portal/our-laws/unacknowledged`,
    //   },
    //   {
    //     title: formatMessage({ id: 'law-portal.statistics.sub-menu.changes.acknowledged' }),
    //     to: `/law-portal/our-laws/acknowledged`,
    //   },
    // ]);
    setHeader(formatMessage({ id: 'law-portal.our-laws.header', defaultMessage: 'Our laws' }));
  }, [setHeader, formatMessage]);

  const handleExport = async () => {
    const response = await api.ExportLawPortalLaws();
    const blob = await response?.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    let fileName = 'LawsExport.xlsx';
    if (contentDisposition && contentDisposition.includes('attachment')) {
      const matches = /filename=([^;]*)/.exec(contentDisposition);
      if (matches && matches[1]) {
        fileName = matches[1];
      }
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
  };

  useEffect(() => {
    const queryParamsMap = {
      status: 'status',
      companyUnit: 'companyUnit',
      complianceUsers: 'complianceUsers',
      processes: 'processes',
      aspects: 'aspects',
      category: 'category',
      area: 'area',
      tags: 'tags',
    };
    Object.entries(queryParamsMap).forEach(([queryKey, stateKey]) => {
      const queryValues = queryParams.getAll(queryKey);
      if (queryValues.length) {
        setFilter(stateKey, queryValues.join(',').split(',').map(num => num.toString()));
      }
    });
    setIsFiltersInitialized(true);
  }, []);

  const renderDate = (date?: Date) => {
    return date ? moment(date).format('ll') : undefined;
  };

  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const updatedQueryParams = Object.keys(filters)
      .filter(key => filters[key]?.join() !== undefined && filters[key]?.join() !== "")
      .map(key => `${key}=${encodeURIComponent(filters[key]?.join())}`)
      .join('&');
    navigate({
      pathname: '',
      search: `?${updatedQueryParams}`,
    });
  }, [filters, navigate]);

  // ✅ FIX: useMemo re-runs whenever formatMessage changes (i.e., on language switch)
  // Previously useState only ran once at mount, so titles were stale after language change
  const baseColumns = useMemo(() => [
    {
      title: formatMessage({ id: 'law-portal.our-laws.table.number' }),
      field: 'number',
      hidden: false,
    },
    {
      title: formatMessage({ id: 'law-portal.our-laws.table.law' }),
      field: 'name',
      render: (row: any) => <RepealableName name={row.name} effectiveTo={row.effectiveTo} />,
      hidden: false,
    },
    {
      title: formatMessage({ id: 'law-portal.our-laws.table.description' }),
      field: 'description',
      render: (row: any) => <HtmlView variant="body2" value={row.description} />,
      sorting: false,
      hidden: false,
    },
    {
      title: formatMessage({ id: 'law-portal.our-laws.table.changeNumber' }),
      field: 'changeNumber',
      hidden: false,
    },
    {
      title: formatMessage({ id: 'law-portal.our-laws.table.changeEffectiveFrom' }),
      field: 'changeEffectiveFrom',
      render: (row: any) => renderDate(row.changeEffectiveFrom),
      hidden: false,
    },
    {
      title: formatMessage({ id: 'law-portal.our-laws.table.notes' }),
      field: 'notes',
      render: (row: any) => <OwnNotes {...row} compact />,
      sorting: false,
      hidden: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance.report.table.col' }),
      field: 'responsible',
      render: (row: any) => {
        if (row.responsible) {
          const responsiblearr = row?.responsible?.split(',');
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {responsiblearr?.map((val: any, i: number) => <span key={i}>{val}</span>)}
            </div>
          );
        }
        return "-";
      },
      sorting: false,
      hidden: false,
    },
    {
      title: formatMessage({ id: 'law-portal.law.table.latest-compliance-control' }),
      field: 'compilancelog',
      render: (row: any) => (
        <Button
          onClick={(event) => {
            event.stopPropagation();
            setLawIdForCompliance(row.id);
            setOpen(true);
          }}
          disabled={row?.logCount == null || row?.logCount == 0}
        >
          {formatMessage({ id: "law-portal.our-laws.table-row.latest-compliance-control.button.name", defaultMessage: "Compliance Log" })}
        </Button>
      ),
      sorting: false,
      hidden: false,
    },
    {
      // title: formatMessage({ id: 'utils.filters.processes' }),
      title: getProcessName(formatMessage),
      field: 'processes',
      render: (row: any) => {
        if (row.processes) {
          const processarr = row?.processes?.split(',');
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {processarr?.map((val: any, i: number) => <span key={i}>{val}</span>)}
            </div>
          );
        }
        return "-";
      },
      sorting: false,
      hidden: false,
    },
    {
      // title: formatMessage({ id: 'utils.filters.aspects' }),
      title: getAspectName(formatMessage),
      field: 'aspects',
      render: (row: any) => {
        if (row.aspects) {
          const aspectsarr = row?.aspects?.split(',');
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {aspectsarr?.map((val: any, i: number) => <span key={i}>{val}</span>)}
            </div>
          );
        }
        return "-";
      },
      sorting: false,
      hidden: false,
    },
    {
      // title: formatMessage({ id: 'utils.filters.tags' }),
      title: getTagName(formatMessage),
      field: 'tags',
      render: (row: any) => {
        if (row.tags) {
          const tagsarr = row?.tags?.split(',');
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {tagsarr?.map((val: any, i: number) => <span key={i}>{val}</span>)}
            </div>
          );
        }
        return "-";
      },
      sorting: false,
      hidden: false,
    },
    {
      title: formatMessage({ id: 'law-portal.our-laws.table.area' }),
      field: 'area',
      hidden: false,
    },
    {
      title: (
        <Button className='custom-save-button' onClick={() => handleSubmit()}>
          {formatMessage({ id: 'form.save' })}
        </Button>
      ),
      field: 'button',
      hidden: true,
    },
  ], [formatMessage, getProcessName, getAspectName, getTagName]); // ✅ re-runs on language change

  // ✅ FIX: Merge base column definitions with user's hidden preferences
  // hiddenFields overrides the default hidden value per field
  const tableColumn = useMemo(() =>
    baseColumns.map(col => ({
      ...col,
      hidden: col.field in hiddenFields ? hiddenFields[col.field] : col.hidden,
    })),
    [baseColumns, hiddenFields]
  );

  const getColumn = async (id: any, lawName: any) => {
    const item = await api.getSaveColumn(id, lawName);
    if (item?.length) {
      const newHidden: Record<string, boolean> = {};
      baseColumns.forEach((col: any) => {
        if (col.field !== 'button') {
          // If field is in saved list → show (false), else hide (true)
          newHidden[col.field] = !item.includes(col.field);
        }
      });
      setHiddenFields(newHidden);
    }
  };

  useEffect(() => {
    const data: any = localStorage.getItem('user');
    const user = JSON.parse(data);
    if (user) {
      getColumn(user?.id, 1);
    }
  }, []);

const handleSubmit=()=>{
  let checkItem:any=[]
  tableColumn.forEach((val:any)=>{
    if(val?.hidden==false&&val?.field!='button'){
      checkItem.push(val?.field)
    }
  })
  let payload={
  TableValue: 1,
  Columns: [...checkItem]
}
if(checkItem?.length){
  api.saveColumn(payload)
}else{
  enqueueSnackbar(intl.formatMessage({ id:'law-portal.minimum_one_select_checkbox' }), {
    variant: 'error',
  });
  
}
}

  const handleDataFetch = useCallback(
    async (query: Query<LawData>) => {
      if (!isFiltersInitialized) {
        return { data: [], page: 0, totalCount: 0 };
      }
      return await api.searchLawPortalLaws<LawData>({
        orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
        orderBy: query.orderBy !== undefined ? query.orderBy.field : 'number',
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
        filters: filters,
      } as IGridQueryRequest);
    },
    [api, filters, isFiltersInitialized],
  );

  const handleRefreshTable = useCallback(() => {
    tableRef.current && (tableRef.current as any).onQueryChange();
  }, []);

  return (
    <div className={classes.listWrapper}>
      <CustomerLawListActions onDeleteComplete={handleRefreshTable} />
      <LawListFilter filters={filters} setFilter={setFilter} />
      <hr style={{ marginTop: '25px', marginBottom: '25px' }} />
      <MTable
        tableRef={tableRef}
        columns={tableColumn}
        data={handleDataFetch}
        onRowClick={(_, row) => row && navigate(`/law-portal/our-laws/${row.id}`)}
        actionLabel={enableCompliance ? formatMessage({ id: 'law-portal.our-laws.table.compliance' }) : undefined}
        actions={
          complianceEnabled
            ? [
              (row: LawData) => ({
                icon: () => <Checkbox checked={hasCompliance((law) => law.id === row.id)} />,
                onClick: () => toggleCompliance(row),
                hidden: !isComplianceUserFor(row.id),
              }),
            ]
            : []
        }
        components={{
          Toolbar: (props) => (
            <div className={classes.toolbar}>
              <MTableToolbar {...props} />
              <SaveAlt style={{ cursor: 'pointer' }} onClick={() => handleExport()} />
            </div>
          ),
        }}
      />
      {open && lawIdForCompliance !== "" && (
        <LatestComplianceControlDialog
          open={open}
          lawId={lawIdForCompliance}
          setOpen={(val: boolean) => {
            setOpen(val);
            setLawIdForCompliance("");
          }}
        />
      )}
      {showScrollTop && (
        <Tooltip title={formatMessage({ id: "bottom.to.top.btn.tooltip.text", defaultMessage: "Back to top" })}>
          <Button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={classes.topButton}
          >
            <ArrowUpward style={{ fontSize: 20 }} />
          </Button>
        </Tooltip>
      )}
    </div>
  );
};
